/**
 * Safe HTML Preview Component
 *
 * Renders HTML content in a sandboxed iframe (srcdoc) so that scripts and other
 * dangerous behavior are disabled.
 */

import React from 'react';
import { Box, Text, Center } from '@mantine/core';

export interface HtmlPreviewProps {
    /** Raw HTML string to render in the iframe */
    content: string;
    /** Message when content is empty */
    emptyMessage?: string;
    /** Height of the preview area (e.g. '100%', '400px') */
    height?: string | number;
    className?: string;
    style?: React.CSSProperties;
}

const DEFAULT_EMPTY = '<html><body><p>No content to preview</p></body></html>';

export const HtmlPreview: React.FC<HtmlPreviewProps> = ({
    content,
    emptyMessage = 'No content to display',
    height = '100%',
    className,
    style
}) => {
    const trimmed = (content ?? '').trim();
    const isEmpty = !trimmed;

    if (isEmpty) {
        return (
            <Center
                style={{
                    height: height ?? '200px',
                    border: '1px solid var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    padding: 'var(--mantine-spacing-sm)',
                    ...style
                }}
                className={className}
            >
                <Text size="sm" c="dimmed">
                    {emptyMessage}
                </Text>
            </Center>
        );
    }

    const needsWrap = !/^\s*<(!DOCTYPE|html|meta\s)/i.test(trimmed);
    const doc = needsWrap
        ? `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${trimmed}</body></html>`
        : trimmed;

    return (
        <Box w="100%" style={{ height: height ?? '100%', overflow: 'hidden', ...style }} className={className}>
            <iframe
                srcDoc={doc}
                sandbox=""
                title="HTML Preview"
                style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block'
                }}
            />
        </Box>
    );
};
